import { z } from 'zod';
import {
  type DisplayReference,
  DisplayReferenceSchema,
  UUIDSchema,
} from '../domain/models';
import {
  createPaginatedResponse,
  PaginationSchema,
} from '../domain/pagination';

export const AllergyTypeSchema = z.enum([
  'medication',
  'food',
  'environment',
  'biologic',
]);

export const AllergyStatusSchema = z.enum(['active', 'inactive', 'resolved']);

export const AllergySeveritySchema = z.enum(['mild', 'severe', 'unknown']);

export type AllergySeverity = z.infer<typeof AllergySeveritySchema>;

export const AllergyTypeArraySchema = AllergyTypeSchema;

export const AllergyCodeSchema = z.object({
  name: z.string(),
  code: z.string(),
  severity: AllergySeveritySchema,
});

export const AllergyAdmissionCreateRequestSchema = AllergyCodeSchema.array();

export const MedpraxAllergySchema = z.object({
  name: z.string(),
  code: z.string(),
  objectID: z.string(),
});

export const AllergySearchSchema = PaginationSchema.extend({
  status: AllergyStatusSchema.optional(),
  patientId: z.string().uuid(),
});

export const AllergySchema = z.object({
  name: z.string(),
  code: z.string(),
  admission: DisplayReferenceSchema.optional(),
  patient: DisplayReferenceSchema,
  recorder: DisplayReferenceSchema,
  status: AllergyStatusSchema,
  type: AllergyTypeArraySchema,
  severity: AllergySeveritySchema.optional(),
});

export const AllergyWithIdSchema = AllergySchema.extend({
  id: UUIDSchema,
});

export const AllergyWithIdArraySchema = z.object({
  items: AllergyWithIdSchema.array(),
});

export const AllergyCreateSchema = z.object({
  name: z.string(),
  code: z.string(),
  patient: UUIDSchema,
  admission: UUIDSchema.optional(),
  recorder: UUIDSchema.optional(),
  severity: AllergySeveritySchema,
});

export const AllergyCreateRequestSchema = z.object({
  patient: UUIDSchema,
  admission: UUIDSchema.optional(),
  items: AllergyCodeSchema.array(),
});

export const UpdateAllergySchema = z.object({
  id: UUIDSchema,
  status: AllergyStatusSchema,
  severity: AllergySeveritySchema,
});

export const AllergyGetAllResponseSchema =
  createPaginatedResponse(AllergyWithIdSchema);

export type AllergyAdmissionCreateRequest = z.infer<
  typeof AllergyAdmissionCreateRequestSchema
>;

export type MedpraxAllergy = z.infer<typeof MedpraxAllergySchema>;

export type AllergyCode = z.infer<typeof AllergyCodeSchema>;

export type Allergy = z.infer<typeof AllergySchema>;
export type AllergyWithId = z.infer<typeof AllergyWithIdSchema>;
export type AllergyWithIdArray = z.infer<typeof AllergyWithIdArraySchema>;
export type AllergyStatus = z.infer<typeof AllergyStatusSchema>;
export type AllergyType = z.infer<typeof AllergyTypeSchema>;

export interface PartialAllergy extends Partial<Allergy> {
  patient: DisplayReference;
}

export type AllergySearch = z.infer<typeof AllergySearchSchema>;
export type AllergyCreate = z.infer<typeof AllergyCreateSchema>;
export type AllergyCreateRequest = z.infer<typeof AllergyCreateRequestSchema>;
export type AllergyUpdate = z.infer<typeof UpdateAllergySchema>;

export type AllergyGetResponse = AllergyWithId;
export type AllergyGetAllResponse = z.infer<typeof AllergyGetAllResponseSchema>;
